import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { writeFetch, getSession } from '../../../api';
import { useAuth } from '../../../context/AuthContext';
import { secureUpload } from '../../../services/s3UploadService';

const { FiX, FiPlus, FiCheck, FiDollarSign, FiClock, FiImage, FiLoader, FiUpload, FiTrash2, FiSettings, FiEdit2, FiMapPin, FiVideo } = FiIcons;

const LISTINGS_TABLE = 'merchant_listings_1777866100042';
const DEFAULT_CATEGORIES = ['Funeral Planning','Legal Services','Grief Counseling','Venue Rental','Florist','Other'];

const DEFAULTS = {
  title: '', description: '', price: '', category: 'Funeral Planning',
  duration_minutes: '60',
  image_url: 'https://images.unsplash.com/photo-1572889815049-74d306b3a0c5?q=80&w=800&auto=format&fit=crop',
  appointment_type: 'physical',
  pricing_model: 'paid'
};

const AddListingModal = ({ isOpen, onClose, onSuccess, editData, initialServiceType = 'bookable', initialPricingModel = 'paid' }) => {
  const { permissions } = useAuth();
  const isAdmin = permissions?.isGlobalAdmin?.();

  const [categories, setCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('merchant_service_categories');
      return saved ? JSON.parse(saved) : DEFAULT_CATEGORIES;
    } catch (e) {
      return DEFAULT_CATEGORIES;
    }
  });

  const [form, setForm] = useState({
    ...DEFAULTS,
    category: categories[0] || 'Other',
    service_type: initialServiceType,
    pricing_model: initialPricingModel,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Category Management State
  const [isManagingCat, setIsManagingCat] = useState(false);
  const [newCat, setNewCat] = useState('');

  // Upload Management State
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (editData) {
      const catList = [...categories];
      if (editData.category && !catList.includes(editData.category)) {
        catList.push(editData.category);
        setCategories(catList);
        localStorage.setItem('merchant_service_categories', JSON.stringify(catList));
      }

      setForm({
        title: editData.title || '',
        description: editData.description || '',
        price: editData.price || '',
        category: editData.category || catList[0] || 'Other',
        duration_minutes: editData.duration_minutes || '60',
        image_url: editData.image_url || '',
        service_type: editData.service_type || initialServiceType,
        pricing_model: editData.pricing_model || initialPricingModel,
        appointment_type: editData.appointment_type || 'physical',
      });
    } else {
      setForm({
        ...DEFAULTS,
        category: categories[0] || 'Other',
        service_type: initialServiceType,
        pricing_model: initialPricingModel,
      });
    }
  }, [editData, isOpen, categories, initialServiceType, initialPricingModel]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSaveCategories = (newCategories) => {
    setCategories(newCategories);
    localStorage.setItem('merchant_service_categories', JSON.stringify(newCategories));
    if (!newCategories.includes(form.category) && newCategories.length > 0) {
      set('category', newCategories[0]);
    }
  };

  const handleAddCategory = () => {
    if (!newCat.trim()) return;
    if (categories.includes(newCat.trim())) return;
    handleSaveCategories([...categories, newCat.trim()]);
    setNewCat('');
  };

  const handleDeleteCategory = (cat) => {
    handleSaveCategories(categories.filter(c => c !== cat));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }
    
    setIsUploading(true);
    setError('');
    
    try {
      const user = getSession();
      const { s3Url } = await secureUpload({
        file,
        ownerId: user?.id || 'anonymous',
        category: 'merchant-listing',
        onProgress: (pct) => setUploadProgress(pct)
      });
      set('image_url', s3Url);
    } catch (err) {
      setError(err.message || 'Failed to upload image. Try using a URL instead.');
    }
    
    setIsUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const user = getSession();
      const ownerId = user?.id || 'anonymous';

      let allListings = [];
      try {
        const checkRes  = await writeFetch(`read/${LISTINGS_TABLE}?owner_id=${ownerId}&limit=200`);
        const checkData = await checkRes.json();
        allListings = checkData.data || [];
      } catch (err) { /* silent */ }
      
      const localCheck = JSON.parse(localStorage.getItem(`mock_listings_${ownerId}`) || '[]');
      const combined = [...allListings, ...localCheck];

      const existing = combined.find(
        l => l.title?.toLowerCase().trim() === form.title.toLowerCase().trim() && (!editData || l.id !== editData.id)
      );

      if (existing) {
        setError('A listing with this title already exists. Please use a different title.');
        setIsSubmitting(false);
        return;
      }

      const listingPayload = {
        owner_id:         ownerId,
        title:            form.title.trim(),
        description:      form.description.trim(),
        price:            form.pricing_model === 'free' ? 0 : (parseFloat(form.price) || 0),
        category:         form.category,
        duration_minutes: parseInt(form.duration_minutes, 10) || 60,
        image_url:        form.image_url.trim(),
        service_type:     form.service_type,
        pricing_model:    form.pricing_model,
        appointment_type: form.appointment_type,
        updated_at:       new Date().toISOString(),
      };

      if (editData) {
        try {
          await writeFetch(`update/${LISTINGS_TABLE}/${editData.id}`, {
            method: 'PUT',
            body: JSON.stringify(listingPayload),
          });
        } catch (err) {}

        const local = JSON.parse(localStorage.getItem(`mock_listings_${ownerId}`) || '[]');
        const updatedLocal = local.map(l => l.id === editData.id ? { ...l, ...listingPayload } : l);
        localStorage.setItem(`mock_listings_${ownerId}`, JSON.stringify(updatedLocal));

      } else {
        const newListing = { ...listingPayload, id: crypto.randomUUID(), is_active: 1, created_at: new Date().toISOString() };
        try {
          await writeFetch(`create/${LISTINGS_TABLE}`, {
            method: 'POST',
            body: JSON.stringify(newListing),
          });
        } catch (err) {}

        const local = JSON.parse(localStorage.getItem(`mock_listings_${ownerId}`) || '[]');
        const nextLocal = local.filter(l => l.title !== newListing.title);
        localStorage.setItem(`mock_listings_${ownerId}`, JSON.stringify([newListing, ...nextLocal]));
      }

      onSuccess?.();
      onClose();
      setForm({ ...DEFAULTS, category: categories[0] || 'Other', service_type: initialServiceType, pricing_model: initialPricingModel });
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <motion.div initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-brand-dark flex flex-col max-h-[90vh]">

        <div className="p-8 border-b border-brand-light flex justify-between items-center bg-brand-light/30 shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent-gold text-white rounded-2xl flex items-center justify-center shadow-lg">
              <SafeIcon icon={editData ? FiEdit2 : FiPlus} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-2xl font-serif text-accent-navy">{editData ? 'Edit Service' : 'Add New Service'}</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Configure availability and pricing</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full text-text-muted">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto custom-scrollbar">
          {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>}

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Service Title</label>
            <input required className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm"
              placeholder="e.g. Legal Will Consultation" value={form.title} onChange={e => set('title', e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Pricing Model</label>
              <div className="flex bg-brand-light border-2 border-brand-dark rounded-2xl p-1 relative">
                 <button type="button" onClick={() => set('pricing_model', 'paid')} className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${form.pricing_model === 'paid' ? 'bg-accent-navy text-white shadow-md' : 'text-text-muted hover:bg-white'}`}>Paid</button>
                 <button type="button" onClick={() => set('pricing_model', 'free')} className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${form.pricing_model === 'free' ? 'bg-green-600 text-white shadow-md' : 'text-text-muted hover:bg-white'}`}>Free</button>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Price ($)</label>
              <div className="relative">
                <SafeIcon icon={FiDollarSign} className="absolute left-5 top-1/2 -translate-y-1/2 text-accent-gold" />
                <input type="number" min="0" step="0.01" required={form.pricing_model === 'paid'} disabled={form.pricing_model === 'free'}
                  className="w-full pl-12 pr-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm disabled:opacity-50"
                  placeholder={form.pricing_model === 'free' ? "0.00" : "0.00"} value={form.pricing_model === 'free' ? '' : form.price} onChange={e => set('price', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Appointment Type</label>
              <select className="w-full px-5 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm"
                value={form.appointment_type} onChange={e => set('appointment_type', e.target.value)}>
                <option value="physical">Physical (In-Person)</option>
                <option value="virtual">Virtual (Zoom / Meet)</option>
                <option value="hybrid">Hybrid (Customer Choice)</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Duration (Mins)</label>
              <div className="relative">
                <SafeIcon icon={FiClock} className="absolute left-5 top-1/2 -translate-y-1/2 text-accent-gold" />
                <input type="number" min="1" required
                  className="w-full pl-12 pr-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm"
                  placeholder="60" value={form.duration_minutes} onChange={e => set('duration_minutes', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="space-y-2 flex flex-col">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Category</label>
              <button type="button" onClick={() => setIsManagingCat(!isManagingCat)} className="text-[10px] text-accent-gold hover:underline flex items-center gap-1">
                <SafeIcon icon={FiSettings} className="w-3 h-3" />
                {isManagingCat ? 'Done Editing' : 'Manage Categories'}
              </button>
            </div>
            
            <AnimatePresence>
              {isManagingCat ? (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  className="bg-brand-light border-2 border-brand-dark rounded-2xl p-4 space-y-3 overflow-hidden mt-1">
                  <div className="flex gap-2">
                    <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="New category name"
                      className="flex-1 px-4 py-2 bg-white border border-brand-dark rounded-xl outline-none text-sm focus:border-accent-gold" />
                    <button type="button" onClick={handleAddCategory} disabled={!newCat.trim()}
                      className="px-4 py-2 bg-accent-navy text-white rounded-xl text-xs font-bold hover:bg-accent-gold transition-colors disabled:opacity-50">
                      Add
                    </button>
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-2 pr-2">
                    {categories.map(cat => (
                      <div key={cat} className="flex items-center justify-between bg-white px-3 py-2 rounded-xl border border-brand-dark">
                        <span className="text-sm text-accent-navy">{cat}</span>
                        <button type="button" onClick={() => handleDeleteCategory(cat)} className="text-red-500 hover:text-red-700">
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <select className="w-full px-5 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm mt-1"
                  value={form.category} onChange={e => set('category', e.target.value)}>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
            </AnimatePresence>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Description</label>
            <textarea required rows={3}
              className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none resize-none text-sm leading-relaxed"
              placeholder="Describe the service..." value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy flex items-center gap-2">
              <SafeIcon icon={FiImage} className="w-3.5 h-3.5" /> Service Image
            </label>
            <div className="flex gap-2">
              <input required
                className="flex-1 px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm"
                placeholder="https://… or click upload" value={form.image_url} onChange={e => set('image_url', e.target.value)} />
              
              <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
              
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading}
                className="px-6 py-4 bg-accent-navy text-white rounded-2xl hover:bg-accent-navy/90 transition-colors flex items-center justify-center gap-2 shadow-md disabled:opacity-70 font-black text-[10px] uppercase tracking-widest shrink-0">
                {isUploading ? (
                  <>
                    <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                    <span className="text-xs font-bold">{uploadProgress}%</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiUpload} className="w-4 h-4" />
                    <span className="hidden sm:inline">Upload</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="pt-4 flex gap-3 shrink-0">
            <button type="button" onClick={onClose}
              className="flex-1 py-4 border-2 border-brand-dark rounded-2xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-navy transition-all">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 py-4 bg-accent-gold text-accent-navy rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy hover:text-white transition-all shadow-lg">
              {isSubmitting
                ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                : <><SafeIcon icon={FiCheck} className="w-4 h-4" /> {editData ? 'Save Changes' : 'Save Service'}</>}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AddListingModal;